import { Link } from 'lego-on-react';
import * as React from 'react';

import { Bem } from '../../service/Bem/Bem';
import { UrlHelpers } from '../../service/UrlHelpers';
import { PcodeRenderData } from '../../typings/pcode';
import { ModalDecorator } from '../ModalDecorator/ModalDecorator';
import { Shape2, Shape2Icon } from '../Shape2/Shape2';

import './VideoCanvasIFrame.css';

export interface VideoCanvasIFrameProps {
    isVisible?: boolean;
    clientId?: string;
    pcodeRenderData: PcodeRenderData;
    onCreativeIdUpdate: (creativeId: string) => void;
    onClose: () => void;
    canvasDomain: string;
    canvasType: VideoCanvasType;
    canvasPageType: VideoCanvasPageType;
}

export enum VideoCanvasMessage {
    ready = 'ready',
    close = 'close',
    creativesSelect = 'creatives-select'
}

export enum VideoCanvasType {
    creatives = 'creatives',
    cpm_audio = 'cpm_audio',
    cpc = 'cpc',
    cpm = 'cpm',
    cpm_outdoor = 'cpm_outdoor',
    cpm_indoor = 'cpm_indoor'
}

export enum VideoCanvasPageType {
    presets = 'presets',
    creatives = 'creative'
}

export class VideoCanvasIFrame extends React.PureComponent<
    VideoCanvasIFrameProps
> {
    private modal: React.RefObject<HTMLIFrameElement>;

    constructor(props: VideoCanvasIFrameProps) {
        super(props);

        this.modal = React.createRef();
    }

    componentWillMount() {
        window.addEventListener('message', this.onMessage);
    }

    componentWillUnmount() {
        window.removeEventListener('message', this.onMessage);
    }

    render() {
        const { isVisible, onClose } = this.props;

        return (
            <ModalDecorator
                visible={Boolean(isVisible)}
                theme={'clear'}
                cls={Bem.cls('video-canvas-iframe', 'modal')}
                onOutsideClick={onClose}
            >
                <Link
                    cls={Bem.cls('video-canvas-iframe', 'close')}
                    theme="pseudo"
                    onClick={onClose}
                >
                    <Shape2 icon={Shape2Icon.close} width={15} height={15} />
                </Link>
                {isVisible && (
                    <iframe
                        ref={this.modal}
                        className={Bem.cls('video-canvas-iframe', 'iframe')}
                        frameBorder="0"
                        src={this.createVideoCanvasUrl()}
                    />
                )}
            </ModalDecorator>
        );
    }

    private createVideoCanvasUrl = () => {
        const { canvasDomain, canvasType, canvasPageType } = this.props;

        const data = this.getDataForVideoCanvasIframe();
        const pageType = this.getPageTypeVideoCanvasIframe();

        switch (canvasType) {
            case VideoCanvasType.cpc:
            case VideoCanvasType.cpm:
            case VideoCanvasType.creatives:
                return UrlHelpers.getCanvasIframeUrl(
                    canvasDomain,
                    pageType,
                    data
                );
            case VideoCanvasType.cpm_indoor:
                return UrlHelpers.getIndoorCreativePresentsUrl(
                    canvasDomain,
                    pageType,
                    data,
                    canvasPageType
                );
            case VideoCanvasType.cpm_outdoor:
                return UrlHelpers.getOutdoorCreativePresentsUrl(
                    canvasDomain,
                    pageType,
                    data,
                    canvasPageType
                );
            case VideoCanvasType.cpm_audio:
                return UrlHelpers.getAudioCreativePresetntUrl(
                    canvasDomain,
                    pageType,
                    data,
                    canvasPageType
                );
            default:
                throw new Error('Cannot find VideoCanvasType');
        }
    };

    private getPageTypeVideoCanvasIframe() {
        const { canvasPageType } = this.props;
        switch (canvasPageType) {
            case VideoCanvasPageType.presets:
                return 'presets';
            case VideoCanvasPageType.creatives:
            default:
                return 'creatives';
        }
    }

    private getDataForVideoCanvasIframe = () => {
        const { canvasType, clientId } = this.props;

        let additionalParams = {};
        switch (canvasType) {
            case VideoCanvasType.cpc:
                additionalParams = {
                    cpc: '1'
                };
                break;
            case VideoCanvasType.cpm:
                additionalParams = {
                    cpm: '1'
                };
                break;
            case VideoCanvasType.cpm_outdoor:
                additionalParams = {
                    cpm_outdoor: '1'
                };
                break;
            case VideoCanvasType.cpm_indoor:
                additionalParams = {
                    cpm_indoor: '1'
                };
                break;
            case VideoCanvasType.cpm_audio:
                additionalParams = {
                    cpm_audio: 1
                };
                break;
            case VideoCanvasType.creatives:
            default:
                break;
        }

        return {
            isCompact: true,
            clientId: clientId && clientId.toString(),
            creativeType: 'videoAddition',
            ...additionalParams
        };
    };

    private onMessage = (ev: MessageEvent) => {
        const {
            pcodeRenderData: { ads, type },
            onCreativeIdUpdate,
            onClose,
            isVisible
        } = this.props;
        const message = ev.data.type;
        const ids = ev.data.creativeIds;

        if (
            !isVisible ||
            !this.modal ||
            (this.modal.current &&
                this.modal.current.contentWindow !== ev.source)
        ) {
            return;
        }

        switch (message) {
            case VideoCanvasMessage.close:
                this.resetFocus();
                onClose();
                break;
            case VideoCanvasMessage.ready:
                (ev.source as Window).postMessage(
                    { type: type, data: ads[0] },
                    '*'
                );
                break;
            case VideoCanvasMessage.creativesSelect:
                // available only on VideoCanvasMessage.creativesSelect
                const [id] = ids;

                onCreativeIdUpdate(id);
                break;
            default:
                break;
        }
    };

    // Для сброса focus в iframe с кнопки Отмена
    private resetFocus = () => {
        if (this.modal.current) {
            this.modal.current.focus();
        }
    };
}
